import { isArray, uniqueId } from "lodash-es";
import React, { ReactNode } from "react";

import { ITestable } from "@/browser";

import { IBlurable, IChangeable } from "../../Form";
import { IUploadData } from "./types";

const getUniqueKey = () => {
  return uniqueId("files-");
};

interface Methods {
  onFilesChange: (files?: File | File[] | null) => void;
  onFilesBlur: () => void;
}

interface State extends Methods {
  values: IUploadData[];
}

export interface IFilesUploadRenderProps extends Methods, ITestable {
  values: IUploadData[];
  invalid?: boolean;
}

interface Props extends IChangeable, IBlurable, ITestable {
  children: (value: IFilesUploadRenderProps) => ReactNode;
  invalid?: boolean;
  onUploadDataInit?: (uploadData: IUploadData[]) => void;
  onUploadDataChange?: (uploadData: IUploadData[]) => void;
}

export class FilesUpload extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      values: this.props.value.map((value: any) => this.createOnlyAttachment(value)),
      onFilesChange: this.onFilesChange,
      onFilesBlur: this.onFilesBlur,
    };
  }

  componentDidMount() {
    const { onUploadDataInit } = this.props;
    onUploadDataInit?.(this.state.values);
  }

  handleChange = () => {
    const { onChange } = this.props;

    onChange?.(this.getAttachments());
  };

  handleUploadDataChange = () => {
    const { onUploadDataChange } = this.props;

    onUploadDataChange?.(this.state.values);
  };

  onFilesChange = (value?: File | File[] | null) => {
    if (isArray(value)) {
      const news = value.map(file => this.createOnlyFile(file));
      this.setState({ values: [...this.state.values, ...news] }, this.handleUploadDataChange);
      return;
    }

    if (value) {
      this.setState(
        { values: [...this.state.values, this.createOnlyFile(value as File)] },
        this.handleUploadDataChange,
      );
      return;
    }
  };

  onFilesBlur = () => {
    const { onBlur } = this.props;

    if (onBlur) {
      onBlur();
    }
  };

  createOnlyFile = (file: File): IUploadData => {
    const key = getUniqueKey();
    return { key, file, onUpload: this.createOnUpload(key), onRemove: this.createOnRemove(key) };
  };

  createOnlyAttachment = (attachment: any, onRemove = this.onRemove, key = getUniqueKey()): IUploadData => {
    return { key, attachment, onRemove };
  };

  createOnUpload = (key: string) => (attachment: any) => {
    const values = this.state.values.map(v =>
      v.key === key ? this.createOnlyAttachment(attachment, v.onRemove, key) : v,
    );
    this.setState({ values }, () => {
      this.handleChange();
      this.handleUploadDataChange();
    });
  };

  createOnRemove = (key: string) => () => {
    this.onRemove(key);
  };

  onRemove = (key: string) => {
    const values = this.state.values.filter(v => v.key !== key);
    this.setState({ values }, () => {
      this.handleChange();
      this.handleUploadDataChange();
    });
  };

  getAttachments = () => {
    const { values } = this.state;
    return values.filter(v => v.attachment).map(v => v.attachment);
  };

  render() {
    const { children, invalid, testName } = this.props;

    return children({ ...this.state, invalid, testName });
  }
}
